import { AccordionItemWrapper, CardContent, CardFooter, CardHeader, HeaderIconButton, ParentProfileCard, ProfileDescriptionCard } from "../../styles/profile/layout.style"
import { useUser } from "../../contextManager/user.context";
import React, { useMemo, useState } from 'react';
import { PencilFill, CheckLg } from 'react-bootstrap-icons';
import { qualificationOptions } from '../../types/schemas/subjectsandgrades'; // Adjust path
import {

  SubjectsGrid,
  GridHeader,
  GridSubjectLabel,
  GridTickCell,
  SubheaderContainer,
  SubheaderTitle,
  SubheaderLine,
  DescriptionText,
  InfoRow,
  InfoLabel,
  InfoValue,
} from '../../styles/profile/form.style';
import { styled } from '../../styles/stitches.config';
import QualificationsDisplay from './QualificationDisplay'; // 👈 1. IMPORT IT
import useAuth, { ProfilePatch } from '../../hooks/auth.service';
import { ProfileDescriptionForm } from './ProfileDescriptionForm';
import { ParentProfileDescriptionForm } from "./ParentProfileDescriptionForm";
import { ActionButton } from "../../styles/profile/profile.style";
import { getParentProfileStatus } from "../../utils";
import { friendlyTzName } from '../../utils/timezones';




export const ParentProfileDescription = () => {
    const { userDetails } = useUser();
    const { patchProfile } = useAuth();
    const [isEditing, setIsEditing] = useState(false);



    const subjectsByLevel = useMemo(() => {
        if (userDetails?.expertise) {
          const expertiseMap = new Map<string, Set<string>>();
          for (const [subject, levels] of Object.entries(userDetails.expertise)) {
            expertiseMap.set(subject, new Set(levels));
          }
          return expertiseMap;
        }
        return new Map();
      }, [userDetails?.expertise]);
    
      const subjects = useMemo(() => Array.from(subjectsByLevel.keys()).sort(), [
        subjectsByLevel,
      ]);
      
      // **NEW**: A smarter hook that sorts levels by how many subjects are taught for them.
      const sortedLevels = useMemo(() => {
        if (!userDetails?.expertise) return [];
        
        const levelCounts: { [level: string]: number } = {};
    
        // First, count how many times each level appears across all subjects
        Object.values(userDetails.expertise).forEach(levelArray => {
          levelArray.forEach(level => {
            if (!levelCounts[level]) {
              levelCounts[level] = 0;
            }
            levelCounts[level]++;
          });
        });
    
        // Convert to an array of objects and sort by count (descending)
        return Object.keys(levelCounts)
          .map(level => ({ level, count: levelCounts[level] }))
          .sort((a, b) => b.count - a.count);
      }, [userDetails?.expertise]);
    
      // **UPDATED**: This now uses the new sortedLevels to build the column widths dynamically.
      const gridColumns = useMemo(() => {
        const subjectColumn = 'max-content';
        
        // Get the names of the top 2 most popular levels, whatever they may be.
        const topLevels = new Set(sortedLevels.slice(0, 2).map(item => item.level));
    
        const levelColumns = sortedLevels.map(({ level }) => {
          // If the current level is one of the most popular, make it wider.
          if (topLevels.has(level)) {
            return 'minmax(195px, 1.5fr)';
          }
          return 'minmax(130px, 1fr)';
        }).join(' ');
        
        return `${subjectColumn} ${levelColumns}`;
      }, [sortedLevels]); // This now depends on the new sortedLevels hook
    
      const handleSave = async (formData: ProfilePatch) => {
        try {
          await patchProfile(
            formData,
            { loading: 'Saving profile...', success: 'Profile saved.' },
          );

          setIsEditing(false);

        } catch (error) {
          console.error("Save failed:", error);
          // The user already sees a toast from patchProfile, so no need for another alert here.
        }
      };
    
      const handleCancel = () => {
        console.log('Canceling edit.');
        setIsEditing(false); // Switch back to display view
      };

      if (isEditing) {
        return (
          <AccordionItemWrapper isOpen={true}>
          <ParentProfileDescriptionForm 
            onSave={handleSave}
            onCancel={handleCancel}
          />
          </AccordionItemWrapper>
        );
      }

    return (
          <ProfileDescriptionCard>
            <CardHeader>
                About
            <HeaderIconButton onClick={() => setIsEditing(true)}>
                <PencilFill size={18} />
                </HeaderIconButton>
            </CardHeader>
            <CardContent>

            <InfoRow>
          <InfoLabel>Full Name</InfoLabel>
          <InfoValue>{userDetails?.name || 'Not Provided'}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>Email Address</InfoLabel>
          <InfoValue>{userDetails?.email || 'Not Provided'}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>Timezone</InfoLabel>
          <InfoValue>{userDetails?.timezone ? friendlyTzName(userDetails.timezone) : 'Not Provided'}</InfoValue>
        </InfoRow>
            <SubheaderContainer>
                <SubheaderLine />
                <SubheaderTitle>Description</SubheaderTitle>
                <SubheaderLine />
            </SubheaderContainer>

                <DescriptionText
                className={!userDetails?.description ? 'not-provided' : ''}
                >
                {userDetails?.description || 'No description provided yet.'}
            </DescriptionText>
            <SubheaderContainer>
          <SubheaderLine />
          <SubheaderTitle>Required Subjects and Levels</SubheaderTitle>
          <SubheaderLine />
        </SubheaderContainer>

            {subjects.length > 0 ? (
          <>
            

          <SubjectsGrid
            css={{
              display: 'grid', 
              gridTemplateColumns: gridColumns,
            }}
          >
            <GridHeader />
            {/* The headers are now mapped from the new sortedLevels array */}
            {sortedLevels.map(({ level }) => (
              <GridHeader key={level}>{level}</GridHeader>
            ))}
            
            {subjects.map(subject => (
              <React.Fragment key={subject}>
                <GridSubjectLabel>{subject}</GridSubjectLabel>
                {/* The data cells also map from sortedLevels to maintain column order */}
                {sortedLevels.map(({ level }) => {
                  const hasExpertise = !!subjectsByLevel
                    .get(subject)
                    ?.has(level);
                  return (
                    <GridTickCell key={level} ticked={hasExpertise}>
                      {hasExpertise && <CheckLg size={18} />}
                    </GridTickCell>
                  );
                })}
              </React.Fragment>
            ))}
          </SubjectsGrid>
          </>
        ) : (
            <DescriptionText className='not-provided'>
            {'Subjects and levels have not been provided yet.'}
        </DescriptionText>

        )}

            </CardContent>
        </ProfileDescriptionCard>
    )
}